import React, { useMemo } from 'react';
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg';
import { ReactComponent as InfoIcon } from 'assets/icons/info-gray.svg';
import { ReactComponent as DeleteIcon } from 'assets/icons/delete.svg';
import { ReactComponent as PlusIcon } from 'assets/icons/plusIcon.svg';
import { ReactComponent as Pharmacy } from 'assets/icons/pharmacy.svg';
import { DynamicQuestionnaireDependencie } from 'components/dynamicQuestionnaire/interfaces/dynamicQuestionnaireResponse.interface';
import { GetQuestionnaireOutput } from 'graphql/generated/remote-schema-hasura';
import { useQuestionnaireSections } from 'components/questionnaire/hooks/useQuestionnaire';
import { useGetDynamicDeps } from 'components/stepper/useStepperForm';
import { useFieldArray, useFormContext } from 'react-hook-form';
import ButtonComponent from 'components/button/buttonComponent';
import { Medication } from '../useQuestionaireValidations';
import {
  FormSelect,
  FormTextarea,
  FormDatePicker,
} from 'components/stepper/field-components';
import { twJoin, twMerge } from 'tailwind-merge';
import { useGetSelfMedication } from './useGetSelfMedication';
import moment from 'moment/moment';
import { STANDARD_DATE } from 'utilities/dateHelper';
import ControlledCheckbox from 'components/stepper/field-components/FormCheckboxGroup';

interface MedicationsProps {
  getQuestionnaireOutput: GetQuestionnaireOutput;
  sectionIndex: number;
}

interface MedicationItemProps {
  frequencyCounts: string[];
  frequencyTimes: string[];
  index: number;
  field: Medication;
  className?: string;
  labels: Record<string, string>;
  handleRemove: () => void;
  handleSave: (index: number) => void;
  handleEdit: (index: number, field: Medication) => void;
  handleAddMedication: () => Promise<void>;
}

type MedicationOldItemProps = Pick<
  MedicationItemProps,
  'field' | 'index' | 'handleRemove'
>;

const MedicationOldItem: React.FC<MedicationOldItemProps> = ({
  field,
  index,
  handleRemove,
}) => {
  return (
    <div
      className={twMerge(
        'flex flex-col w-full border-2 border-transparent p-30 bg-base-content rounded-lg gap-2.5',
      )}
    >
      <div className="flex flex-col-reverse desktop:flex-row items-center gap-2.5 justify-between">
        <p className="font-semibold flex items-center gap-2.5 text-amber-border text-sm">
          <InfoIcon className="fill-amber-border w-5 h-5 min-w-[20px]" />
          <span>
            Medication form updated. Please replace the old version with a new
          </span>
        </p>
        <div className="flex gap-2.5 items-center">
          <ButtonComponent
            type="underline"
            className="ml-auto"
            testID={`${index}-remove`}
            onClick={handleRemove}
            Icon={DeleteIcon}
            htmlButtonType="button"
            iconPosition="left"
          >
            Remove
          </ButtonComponent>
        </div>
      </div>
      <p className="text-neutral-800 text-h5 font-semibold">{field?.name}</p>
      <p className="flex items-center text-med-gray-3 font-medium">
        <span>
          {field.frequency.count} per {field.frequency.time}
        </span>
      </p>
      <p
        className={twMerge(
          'hidden items-center text-med-gray-3 font-medium',
          field?.startDate && !field?.regularly && 'flex',
        )}
      >
        <span>
          {moment(field?.startDate).format(STANDARD_DATE)} -{' '}
          {moment(field?.endDate).format(STANDARD_DATE)}
        </span>
      </p>
      <p
        className={twMerge(
          'hidden items-center text-med-gray-3 font-medium',
          field?.startDate && field?.regularly && 'flex',
        )}
      >
        {moment(field?.startDate).format(STANDARD_DATE)}
      </p>
      <p
        className={twJoin(
          'text-med-gray-3 font-medium',
          !field?.notes && 'hidden',
        )}
      >
        {field.notes}
      </p>
    </div>
  );
};

const MedicationItem: React.FC<MedicationItemProps> = ({
  frequencyCounts,
  frequencyTimes,
  index,
  field,
  labels,
  handleRemove,
  handleSave,
  handleEdit,
  className,
}) => {
  const { setValue, getValues, watch } = useFormContext();
  const regularly = watch(`medication.${index}.regularly`);

  const {
    medicationsList,
    handleLoadMoreMedications,
    loading,
    isFetchingMore,
    handleSearchChange,
  } = useGetSelfMedication(field.isSaved);

  const frequencyCountOptions = useMemo(
    () => frequencyCounts.map((value) => ({ value, label: value })),
    [frequencyCounts],
  );
  const frequencyTimesOptions = useMemo(
    () => frequencyTimes.map((value) => ({ value, label: value })),
    [frequencyTimes],
  );

  const handleLocalSave = () => {
    const currentSelectedValue = getValues(`medication.${index}.name`);
    const foundOption = medicationsList.find(
      (m) => m.value === currentSelectedValue,
    );

    if (foundOption) {
      setValue(`medication.${index}.label`, foundOption.label, {
        shouldValidate: true,
      });
    }

    handleSave(index);
  };

  const list = useMemo(
    () =>
      [
        { value: field.name, label: field.label ?? '' },
        ...medicationsList.filter(({ value }) => value !== field.name),
      ].filter(({ value }) => !!value),
    [field.label, field.name, medicationsList],
  );

  if (field.isSaved) {
    const name =
      field?.label ||
      medicationsList.find(({ value }) => value === field.name)?.label;

    return (
      <div
        className={twMerge(
          'flex flex-col w-full border-2 border-transparent p-30 bg-base-content rounded-lg gap-2.5',
          className,
        )}
      >
        <div
          className={twMerge(
            'flex flex-col desktop:flex-row items-center gap-2.5 justify-between',
            field?.startDate && 'hidden',
          )}
        >
          <p className="font-semibold flex flex-col-reverse desktop:flex-row items-center gap-2.5 text-amber-border text-sm">
            <InfoIcon className="fill-amber-border w-5 h-5 min-w-[20px]" />
            <span>
              Medication form has been updated. Please add both Start and End
              date fields
            </span>
          </p>
          <div className="flex gap-2.5 items-center">
            <ButtonComponent
              type="underline"
              className="ml-auto"
              testID={`${index}-remove`}
              onClick={handleRemove}
              Icon={DeleteIcon}
              htmlButtonType="button"
              iconPosition="left"
            >
              Remove
            </ButtonComponent>
            <ButtonComponent
              type="underline"
              className="ml-auto"
              testID={`${index}-edit`}
              onClick={() => handleEdit(index, field)}
              iconPosition="left"
              htmlButtonType="button"
              Icon={EditIcon}
            >
              Edit
            </ButtonComponent>
          </div>
        </div>
        <p
          className={twMerge(
            'text-neutral-800 text-h5 font-semibold',
            field?.startDate && 'hidden',
          )}
        >
          {name}
        </p>
        <div
          className={twMerge(
            'hidden desktop:flex-row flex-col-reverse items-center gap-2.5 justify-between',
            field?.startDate && 'flex',
          )}
        >
          <p className="text-neutral-800 text-h5 font-semibold">{name}</p>
          <div className="flex gap-2.5 items-center max-desktop:justify-between max-desktop:w-full">
            <ButtonComponent
              type="underline"
              className="ml-auto"
              testID={`${index}-remove`}
              onClick={handleRemove}
              Icon={DeleteIcon}
              htmlButtonType="button"
              iconPosition="left"
            >
              Remove
            </ButtonComponent>
            <ButtonComponent
              type="underline"
              className="ml-auto"
              testID={`${index}-edit`}
              onClick={() => handleEdit(index, field)}
              iconPosition="left"
              htmlButtonType="button"
              Icon={EditIcon}
            >
              Edit
            </ButtonComponent>
          </div>
        </div>
        <p className="flex items-center text-med-gray-3 font-medium">
          <span>
            {field.frequency.count} per {field.frequency.time}
          </span>
        </p>
        <p
          className={twMerge(
            'hidden items-center text-med-gray-3 font-medium',
            field?.startDate && !field?.regularly && 'flex',
          )}
        >
          <span>
            {moment(field?.startDate).format(STANDARD_DATE)} -{' '}
            {moment(field?.endDate).format(STANDARD_DATE)}
          </span>
        </p>
        <p
          className={twMerge(
            'hidden items-center text-med-gray-3 font-medium',
            field?.startDate && field?.regularly && 'flex',
          )}
        >
          {moment(field?.startDate).format(STANDARD_DATE)}
        </p>
        <p
          className={twJoin(
            'text-med-gray-3 font-medium',
            !field?.notes && 'hidden',
          )}
        >
          {field.notes}
        </p>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-12 p-30 bg-base-content rounded-lg gap-2.5">
      <p className="flex col-span-12 items-center justify-between">
        <span className="text-h5 text-neutral-800 font-semibold">
          Medication
        </span>
        <ButtonComponent
          onClick={handleRemove}
          testID={`${index}-remove`}
          Icon={DeleteIcon}
          htmlButtonType="button"
          iconPosition="left"
          className="!w-fit"
          type="underline"
        >
          Remove
        </ButtonComponent>
      </p>
      <FormSelect
        className="col-span-12"
        name={`medication.${index}.name`}
        label={labels?.name}
        placeholder="Enter or select medication name (e.g. Progesterone)"
        options={list}
        onMenuScrollToBottom={handleLoadMoreMedications}
        onInputChange={handleSearchChange}
        isLoading={loading || isFetchingMore}
        isSearchable
        isRequired
      />
      <p className="flex flex-col desktop:flex-row items-start justify-between col-span-12 gap-2.5 desktop:gap-6 desktop:col-span-10">
        <FormSelect
          name={`medication.${index}.frequency.count`}
          label={labels?.frequencyCounts}
          placeholder="Enter amount"
          withoutTouch={false}
          options={frequencyCountOptions}
          isRequired
        />
        <span className="text-med-gray-3 text-sm mt-11 font-medium max-desktop:hidden">
          per
        </span>
        <FormSelect
          name={`medication.${index}.frequency.time`}
          withoutTouch={false}
          label={'Times'}
          labelClassName="desktop:invisible"
          options={frequencyTimesOptions}
        />
      </p>
      <p className="flex flex-col desktop:flex-row items-center justify-between desktop:gap-4 gap-2.5 w-full col-span-12">
        <FormDatePicker
          name={`medication.${index}.startDate`}
          className="w-full"
          withoutTouch={false}
          label="Start date"
          isRequired
        />
        <FormDatePicker
          name={`medication.${index}.endDate`}
          className="w-full"
          withoutTouch={false}
          label="End date"
          disabled={regularly}
          isRequired
        />
        <ControlledCheckbox
          label="I take it regularly"
          name={`medication.${index}.regularly`}
          wrapperClassName="w-fit whitespace-nowrap mt-6"
        />
      </p>
      <FormTextarea
        wrapperClassName="col-span-12"
        className="min-h-[102px]"
        name={`medication.${index}.notes`}
        placeholder="Enter additional notes"
        label={labels?.notes}
        describe="Please add any additional information about this medication"
      />
      <ButtonComponent
        className="!w-fit col-span-4"
        htmlButtonType="button"
        testID={`${index}-save`}
        onClick={handleLocalSave}
      >
        Save
      </ButtonComponent>
    </div>
  );
};

const Medications: React.FC<MedicationsProps> = ({
  getQuestionnaireOutput,
  sectionIndex,
}) => {
  const {
    control,
    trigger,
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext();

  const { currentSection, questionsById } = useQuestionnaireSections(
    getQuestionnaireOutput,
    sectionIndex,
  );

  const sectionDependencies = (currentSection?.dependencies ??
    []) as DynamicQuestionnaireDependencie[];

  const dynamicDeps = useGetDynamicDeps(sectionDependencies);

  const ids = dynamicDeps.requiredFields.sort();

  const { fields, remove, append, update } = useFieldArray({
    control,
    name: 'medication',
  });

  const { fields: medicationOld, remove: removeMedicationOld } = useFieldArray({
    control,
    name: 'medicationOld',
  });

  const frequencyCounts = useMemo(
    () =>
      questionsById[ids[1]]?.dropdown_combo_answers?.dropdown_1_answers ?? [],
    [ids, questionsById],
  );
  const frequencyTimes = useMemo(
    () =>
      questionsById[ids[1]]?.dropdown_combo_answers?.dropdown_2_answers ?? [],
    [ids, questionsById],
  );

  const labels = useMemo(
    () => ({
      name: questionsById[ids[0]]?.text ?? 'Medication name',
      frequencyCounts: questionsById[ids[2]]?.text ?? 'Frequency',
      frequencyTimes: questionsById[ids[2]]?.text ?? '',
      notes: 'Additional notes',
    }),
    [ids, questionsById],
  );

  const handleAddMedication = async () => {
    append({
      name: '',
      label: '',
      frequency: {
        count: '',
        time: '',
      },
      notes: '',
      isSaved: false,
      regularly: false,
    });
  };

  const handleEdit = (index: number, field: Medication) => {
    update(index, { ...field, isSaved: false });
  };

  const handleSave = async (index: number) => {
    const fieldName = `medication.${index}`;
    const currentValues = getValues(fieldName);

    setValue(`${fieldName}.name`, currentValues?.name, { shouldTouch: true });
    setValue(`${fieldName}.frequency.count`, currentValues?.frequency?.count, {
      shouldTouch: true,
    });
    setValue(`${fieldName}.frequency.time`, currentValues?.frequency?.time, {
      shouldTouch: true,
    });
    setValue(`${fieldName}.startDate`, currentValues?.startDate, {
      shouldTouch: true,
    });
    setValue(`${fieldName}.endDate`, currentValues?.endDate, {
      shouldTouch: true,
    });

    const isValid = await trigger(fieldName);
    if (isValid) {
      update(index, { ...currentValues, isSaved: true });
    }
  };

  if (!fields.length && !medicationOld.length) {
    return (
      <div className="flex text-center flex-col justify-center gap-2.5 py-2.5 px-30">
        <div className="rounded-full bg-hover-blue mx-auto p-4 flex items-center justify-center">
          <Pharmacy className="w-9 h-9 fill-clc-blue" />
        </div>
        <p className="text-h5 text-neutral-800 font-semibold">
          {'No medications added yet'}
        </p>
        <p className="font-medium text-med-gray-3">
          {
            'Add prescription medications, over-the-counter medicines, supplements, or recent treatments you are taking now or took recently. This helps your healthcare provider better understand your condition and build the right treatment plan'
          }
        </p>
        <ButtonComponent
          type="underline"
          testID={'ad-medication'}
          className="font-bold text-sm"
          onClick={handleAddMedication}
          iconPosition="left"
          htmlButtonType="button"
          Icon={PlusIcon}
        >
          Add medication
        </ButtonComponent>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col desktop:gap-30 gap-4">
      {(medicationOld ?? []).map((field, i) => (
        <MedicationOldItem
          field={field as unknown as Medication}
          index={i}
          key={field.id}
          handleRemove={() => removeMedicationOld(i)}
        />
      ))}
      {fields.map((field, i) => (
        <MedicationItem
          className={
            (errors?.medication as unknown as Record<string, string>[])?.[i]
              ? '!border-alert-negative'
              : undefined
          }
          field={field as unknown as Medication}
          index={i}
          frequencyCounts={frequencyCounts}
          frequencyTimes={frequencyTimes}
          labels={labels}
          handleRemove={() => remove(i)}
          handleAddMedication={handleAddMedication}
          handleEdit={handleEdit}
          handleSave={handleSave}
        />
      ))}
      <ButtonComponent
        type="underline"
        testID={'add-medication'}
        onClick={handleAddMedication}
        iconPosition="left"
        htmlButtonType="button"
        Icon={PlusIcon}
      >
        Add medication
      </ButtonComponent>
    </div>
  );
};

export default Medications;
